import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import operationApiInstance from "../../api/OperationApi";
import { dispatchErrorNotification } from "../../helpers/errorDispatch";
import { translateApiError } from "../../i18n/errorCodes";
import type { BillingOperationSummary } from "../../types/billing";
import { refreshStreamSnapshotAfterMutation } from "../refreshStreamSnapshot";
import { finishCommandsForOperation } from "../../helpers/pendingCommand";

export interface OperationsState {
  byStreamId: Record<string, BillingOperationSummary[]>;
  requestId: Record<string, string | undefined>;
  loading: Record<string, boolean>;
  retryLoading: Record<string, Record<string, boolean>>;
  retryError: Record<string, Record<string, string | undefined>>;
  retryRequestId: Record<string, Record<string, string | undefined>>;
  notRetryable: Record<string, Record<string, boolean>>;
}

const initialState: OperationsState = {
  byStreamId: {},
  requestId: {},
  loading: {},
  retryLoading: {},
  retryError: {},
  retryRequestId: {},
  notRetryable: {},
};

// H1: "failed" is a deterministic Stripe decline — terminal like completed/
// cancelled/superseded, never re-entered by a later Retry response.
const terminal = (operation: BillingOperationSummary | undefined) =>
  operation &&
  ["completed", "cancelled", "superseded", "failed"].includes(operation.state)
    ? operation
    : undefined;

export const operationsSlice = createSlice({
  name: "operations",
  initialState,
  reducers: {
    beginOperationsRequest(
      state,
      action: PayloadAction<{ streamId: string; requestId: string }>
    ) {
      state.requestId[action.payload.streamId] = action.payload.requestId;
      state.loading[action.payload.streamId] = true;
    },
    completeOperationsRequest(
      state,
      action: PayloadAction<{
        streamId: string;
        requestId: string;
        operations: BillingOperationSummary[];
      }>
    ) {
      const { streamId, requestId } = action.payload;
      if (state.requestId[streamId] !== requestId) return;
      const previous = state.byStreamId[streamId] ?? [];
      state.byStreamId[streamId] = action.payload.operations
        .filter((operation) => operation.liveStreamId === streamId)
        .map(
          (operation) =>
            terminal(previous.find((item) => item.id === operation.id)) ??
            operation
        );
      state.loading[streamId] = false;
    },
    failOperationsRequest(
      state,
      action: PayloadAction<{ streamId: string; requestId: string }>
    ) {
      if (state.requestId[action.payload.streamId] === action.payload.requestId)
        state.loading[action.payload.streamId] = false;
    },
    replaceStreamOperations(
      state,
      action: PayloadAction<{
        streamId: string;
        operations: BillingOperationSummary[];
      }>
    ) {
      state.byStreamId[action.payload.streamId] =
        action.payload.operations.filter(
          (operation) => operation.liveStreamId === action.payload.streamId
        );
    },
    upsertStreamOperation(
      state,
      action: PayloadAction<BillingOperationSummary>
    ) {
      const operation = action.payload;
      if (!operation.liveStreamId) return;
      const operations = state.byStreamId[operation.liveStreamId] ?? [];
      const index = operations.findIndex((item) => item.id === operation.id);
      if (index < 0) operations.push(operation);
      else operations[index] = terminal(operations[index]) ?? operation;
      state.byStreamId[operation.liveStreamId] = operations;
    },
    beginOperationRetry(
      state,
      action: PayloadAction<{
        streamId: string;
        operationId: string;
        requestId: string;
      }>
    ) {
      const { streamId, operationId, requestId } = action.payload;
      delete state.requestId[streamId];
      state.loading[streamId] = false;
      state.retryLoading[streamId] ??= {};
      state.retryError[streamId] ??= {};
      state.retryRequestId[streamId] ??= {};
      state.retryLoading[streamId][operationId] = true;
      state.retryError[streamId][operationId] = undefined;
      state.retryRequestId[streamId][operationId] = requestId;
    },
    completeOperationRetry(
      state,
      action: PayloadAction<{
        streamId: string;
        operationId: string;
        requestId: string;
        operation: BillingOperationSummary;
      }>
    ) {
      const { streamId, operationId, requestId, operation } = action.payload;
      if (state.retryRequestId[streamId]?.[operationId] !== requestId) return;
      if (
        operation.id !== operationId ||
        (operation.liveStreamId !== null && operation.liveStreamId !== streamId)
      )
        return;
      const operations = state.byStreamId[streamId] ?? [];
      const accepted =
        terminal(operations.find((item) => item.id === operationId)) ??
        operation;
      const index = operations.findIndex((item) => item.id === operationId);
      if (index < 0) operations.push(accepted);
      else operations[index] = accepted;
      state.byStreamId[streamId] = operations;
    },
    failOperationRetry(
      state,
      action: PayloadAction<{
        streamId: string;
        operationId: string;
        requestId: string;
        error: string;
        code?: string;
      }>
    ) {
      const { streamId, operationId, requestId, error, code } = action.payload;
      if (state.retryRequestId[streamId]?.[operationId] === requestId) {
        state.retryError[streamId][operationId] = error;
        if (code === "OPERATION_NOT_RETRYABLE") {
          state.notRetryable[streamId] ??= {};
          state.notRetryable[streamId][operationId] = true;
        }
      }
    },
    finishOperationRetry(
      state,
      action: PayloadAction<{
        streamId: string;
        operationId: string;
        requestId: string;
      }>
    ) {
      const { streamId, operationId, requestId } = action.payload;
      if (state.retryRequestId[streamId]?.[operationId] !== requestId) return;
      delete state.requestId[streamId];
      state.loading[streamId] = false;
      state.retryLoading[streamId][operationId] = false;
      delete state.retryRequestId[streamId][operationId];
    },
    clearOperations(state, action: PayloadAction<string>) {
      delete state.byStreamId[action.payload];
      delete state.requestId[action.payload];
      delete state.loading[action.payload];
      delete state.retryLoading[action.payload];
      delete state.retryError[action.payload];
      delete state.retryRequestId[action.payload];
      delete state.notRetryable[action.payload];
    },
  },
});

export const {
  beginOperationsRequest,
  completeOperationsRequest,
  failOperationsRequest,
  beginOperationRetry,
  completeOperationRetry,
  failOperationRetry,
  finishOperationRetry,
  upsertStreamOperation,
} = operationsSlice.actions;

export const getStreamOperations = createAsyncThunk(
  "operations/getStreamOperations",
  async (streamId: string, { dispatch, requestId }) => {
    dispatch(beginOperationsRequest({ streamId, requestId }));
    try {
      const { data } = await operationApiInstance.list(streamId);
      dispatch(
        completeOperationsRequest({ streamId, requestId, operations: data })
      );
      for (const operation of data) {
        if (terminal(operation) && operation.liveStreamId === streamId)
          finishCommandsForOperation(operation.id, streamId);
      }
      return data;
    } catch (error) {
      dispatch(failOperationsRequest({ streamId, requestId }));
      throw error;
    }
  }
);

export const retryStreamOperation = createAsyncThunk(
  "operations/retryStreamOperation",
  async (
    { streamId, operationId }: { streamId: string; operationId: string },
    { dispatch, getState, requestId }
  ) => {
    dispatch(beginOperationRetry({ streamId, operationId, requestId }));
    try {
      const { data } = await operationApiInstance.retry(streamId, operationId);
      const state = getState() as {
        operations: OperationsState;
        entitlement?: {
          byStreamId: Record<
            string,
            { operation?: BillingOperationSummary | null }
          >;
        };
      };
      if (
        state.operations.retryRequestId[streamId]?.[operationId] === requestId
      ) {
        const accepted =
          terminal(
            state.operations.byStreamId[streamId]?.find(
              (operation) => operation.id === operationId
            )
          ) ??
          terminal(
            state.entitlement?.byStreamId[streamId]?.operation?.id ===
              operationId &&
              (!state.entitlement.byStreamId[streamId]?.operation
                ?.liveStreamId ||
                state.entitlement.byStreamId[streamId]?.operation
                  ?.liveStreamId === streamId)
              ? state.entitlement.byStreamId[streamId]?.operation ?? undefined
              : undefined
          ) ??
          data;
        dispatch(
          completeOperationRetry({
            streamId,
            operationId,
            requestId,
            operation: accepted,
          })
        );
      }
      return data;
    } catch (error: any) {
      dispatch(
        failOperationRetry({
          streamId,
          operationId,
          requestId,
          error: translateApiError(error),
          code: error?.code,
        })
      );
      const state = getState() as { operations: OperationsState };
      if (
        state.operations.retryRequestId[streamId]?.[operationId] === requestId
      )
        dispatchErrorNotification(error);
      throw error;
    } finally {
      const state = getState() as { operations: OperationsState };
      const current =
        state.operations.retryRequestId[streamId]?.[operationId] === requestId;
      dispatch(finishOperationRetry({ streamId, operationId, requestId }));
      if (current) void dispatch(refreshStreamSnapshotAfterMutation(streamId));
    }
  }
);

export default operationsSlice.reducer;
